import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Heart, ArrowLeft, Users, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { FOUNDER_CONFIG } from '@/types/founder';

const copy = {
  en: {
    title: 'Our Founding Members',
    subtitle: 'These people believed in Say It NOW from the very beginning. Their support made this possible — and their words will echo for years to come.',
    slotsLabel: 'of 150 founding spots taken',
    joinedLabel: 'Joined',
    backHome: 'Back to Home',
    loading: 'Loading...',
    emptyTitle: 'The wall awaits its first names.',
    emptyDesc: 'Founding Member spots are still available — be among the first to join.',
    signUp: 'Become a Founding Member',
    gratitude: 'Thank you for being here from the start.',
  },
  es: {
    title: 'Nuestros Miembros Fundadores',
    subtitle: 'Estas personas creyeron en Say It NOW desde el principio. Su apoyo hizo esto posible — y sus palabras resonarán por años.',
    slotsLabel: 'de 150 lugares fundadores ocupados',
    joinedLabel: 'Se unió',
    backHome: 'Volver al Inicio',
    loading: 'Cargando...',
    emptyTitle: 'El muro espera sus primeros nombres.',
    emptyDesc: 'Los lugares de Miembro Fundador aún están disponibles — sé de los primeros.',
    signUp: 'Conviértete en Miembro Fundador',
    gratitude: 'Gracias por estar aquí desde el principio.',
  },
  fr: {
    title: 'Nos Membres Fondateurs',
    subtitle: 'Ces personnes ont cru en Say It NOW dès le début. Leur soutien a rendu cela possible — et leurs mots résonneront pendant des années.',
    slotsLabel: 'sur 150 places fondatrices prises',
    joinedLabel: 'Rejoint le',
    backHome: 'Retour à l\'accueil',
    loading: 'Chargement...',
    emptyTitle: 'Le mur attend ses premiers noms.',
    emptyDesc: 'Des places de Membre Fondateur sont encore disponibles — soyez parmi les premiers.',
    signUp: 'Devenir Membre Fondateur',
    gratitude: 'Merci d\'être là depuis le début.',
  },
  pt: {
    title: 'Nossos Membros Fundadores',
    subtitle: 'Essas pessoas acreditaram no Say It NOW desde o início. Seu apoio tornou isso possível — e suas palavras ecoarão por anos.',
    slotsLabel: 'de 150 vagas fundadoras preenchidas',
    joinedLabel: 'Entrou em',
    backHome: 'Voltar ao Início',
    loading: 'Carregando...',
    emptyTitle: 'O mural aguarda seus primeiros nomes.',
    emptyDesc: 'Vagas de Membro Fundador ainda estão disponíveis — seja um dos primeiros.',
    signUp: 'Torne-se um Membro Fundador',
    gratitude: 'Obrigado por estar aqui desde o início.',
  },
  ar: {
    title: 'أعضاؤنا المؤسسون',
    subtitle: 'هؤلاء الأشخاص آمنوا بـ Say It NOW منذ البداية. دعمهم جعل هذا ممكناً — وكلماتهم ستتردد لسنوات قادمة.',
    slotsLabel: 'من 150 مكان تأسيسي مشغول',
    joinedLabel: 'انضم في',
    backHome: 'العودة للرئيسية',
    loading: 'جارٍ التحميل...',
    emptyTitle: 'الجدار ينتظر أسماءه الأولى.',
    emptyDesc: 'أماكن الأعضاء المؤسسين لا تزال متاحة — كن من الأوائل.',
    signUp: 'كن عضواً مؤسساً',
    gratitude: 'شكراً لوجودك منذ البداية.',
  },
  ja: {
    title: '創設メンバー',
    subtitle: 'これらの人々は最初からSay It NOWを信じてくれました。彼らのサポートがこれを可能にし、その言葉は何年も響き続けます。',
    slotsLabel: '/ 150の創設枠が埋まっています',
    joinedLabel: '参加日',
    backHome: 'ホームに戻る',
    loading: '読み込み中...',
    emptyTitle: '壁はまだ最初の名前を待っています。',
    emptyDesc: '創設メンバー枠はまだ利用可能です — 最初の一人になりましょう。',
    signUp: '創設メンバーになる',
    gratitude: '最初からいてくれてありがとう。',
  },
  ko: {
    title: '창립 멤버',
    subtitle: '이 분들은 처음부터 Say It NOW를 믿어주셨습니다. 그들의 지지가 이것을 가능하게 했고, 그 말은 수년간 울려 퍼질 것입니다.',
    slotsLabel: '/ 150 창립 자리 차지',
    joinedLabel: '가입일',
    backHome: '홈으로 돌아가기',
    loading: '로딩 중...',
    emptyTitle: '벽은 첫 이름을 기다리고 있습니다.',
    emptyDesc: '창립 멤버 자리가 아직 남아있습니다 — 첫 번째가 되세요.',
    signUp: '창립 멤버 되기',
    gratitude: '처음부터 함께해 주셔서 감사합니다.',
  },
  id: {
    title: 'Anggota Pendiri Kami',
    subtitle: 'Orang-orang ini percaya pada Say It NOW sejak awal. Dukungan mereka memungkinkan semua ini — dan kata-kata mereka akan bergema selama bertahun-tahun.',
    slotsLabel: 'dari 150 tempat pendiri terisi',
    joinedLabel: 'Bergabung',
    backHome: 'Kembali ke Beranda',
    loading: 'Memuat...',
    emptyTitle: 'Dinding menunggu nama-nama pertamanya.',
    emptyDesc: 'Tempat Anggota Pendiri masih tersedia — jadilah yang pertama.',
    signUp: 'Jadilah Anggota Pendiri',
    gratitude: 'Terima kasih telah ada di sini sejak awal.',
  },

  hi: {
    title: 'हमारे संस्थापक सदस्य',
    subtitle: 'इन लोगों ने शुरुआत से ही Say It NOW में विश्वास किया। उनके समर्थन ने इसे संभव बनाया — और उनके शब्द आने वाले वर्षों तक गूंजते रहेंगे।',
    slotsLabel: '150 संस्थापक स्थानों में से भरे गए',
    joinedLabel: 'शामिल हुए',
    backHome: 'होम पर वापस',
    loading: 'लोड हो रहा है...',
    emptyTitle: 'दीवार अपने पहले नामों का इंतजार कर रही है।',
    emptyDesc: 'संस्थापक सदस्य स्थान अभी भी उपलब्ध हैं — शामिल होने वाले पहले लोगों में से एक बनें।',
    signUp: 'एक संस्थापक सदस्य बनें',
    gratitude: 'शुरुआत से यहां रहने के लिए धन्यवाद।',
  },
};

interface PublicFounder {
  slot_number: number;
  qualified_at: string;
  current_plan: string;
  first_name: string | null;
  avatar_url: string | null;
}

export default function FoundersWall() {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const [founders, setFounders] = useState<PublicFounder[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useDocumentHead({
    title: 'Our Founding Members | Say It NOW',
    description: 'Meet the people who believed in Say It NOW from the very beginning. Our founding members make everything possible.',
    meta: [
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: 'Our Founding Members | Say It NOW' },
      { property: 'og:description', content: 'Meet the people who believed in Say It NOW from the very beginning.' },
    ],
  });

  useEffect(() => {
    const fetchFounders = async () => {
      const { data, error } = await supabase.rpc('get_founders_public' as any);
      if (!error && data) {
        setFounders(data as unknown as PublicFounder[]);
      }
      setIsLoading(false);
    };
    fetchFounders();
  }, []);

  const filledSlots = founders.length;
  const fillPercent = Math.min((filledSlots / FOUNDER_CONFIG.maxSlots) * 100, 100);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container flex items-center justify-between h-14 px-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {t.backHome}
            </Button>
          </Link>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Shield className="h-4 w-4 text-primary" />
            <span className="font-medium">{filledSlots} {t.slotsLabel}</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-10 left-1/4 h-[300px] w-[300px] rounded-full bg-primary/6 blur-[100px]"
            animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute bottom-0 right-1/4 h-[250px] w-[250px] rounded-full bg-accent/8 blur-[80px]"
            animate={{ scale: [1.1, 1, 1.1] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        <div className="container relative z-10 py-16 md:py-24 text-center max-w-2xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-primary/10 border border-primary/20 mb-6">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground tracking-tight mb-4">
              {t.title}
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-xl mx-auto">
              {t.subtitle}
            </p>
          </motion.div>

          {/* Progress bar */}
          <motion.div
            className="mt-8 max-w-xs mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70"
                initial={{ width: 0 }}
                animate={{ width: `${fillPercent}%` }}
                transition={{ duration: 1.2, delay: 0.6, ease: 'easeOut' }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1.5">
              <Users className="h-3 w-3" />
              <span className="font-semibold text-foreground">{filledSlots}</span> / {FOUNDER_CONFIG.maxSlots}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founders Grid */}
      <main className="container px-4 pb-20">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
              <Sparkles className="h-6 w-6 text-primary" />
            </motion.div>
            <span className="ml-3 text-muted-foreground">{t.loading}</span>
          </div>
        ) : founders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <Heart className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">{t.emptyTitle}</h2>
            <p className="text-muted-foreground mb-6">{t.emptyDesc}</p>
            <Link to="/signup">
              <Button size="lg" className="gap-2 rounded-2xl">
                <Shield className="h-5 w-5" />
                {t.signUp}
              </Button>
            </Link>
          </motion.div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
              {founders.map((founder, i) => (
                <motion.div
                  key={founder.slot_number}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: Math.min(i * 0.04, 1.5), duration: 0.4 }}
                  className="group"
                >
                  <div className="relative p-5 rounded-2xl bg-card/60 border border-border/40 hover:border-primary/30 hover:bg-card/80 transition-all duration-300 text-center">
                    {/* Slot badge */}
                    <div className="absolute -top-2 -right-2 flex items-center justify-center h-7 w-7 rounded-full bg-primary text-primary-foreground text-[10px] font-bold shadow-lg">
                      #{founder.slot_number}
                    </div>

                    <Avatar className="h-16 w-16 mx-auto mb-3 ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all">
                      <AvatarImage src={founder.avatar_url || undefined} alt={founder.first_name || 'Founder'} />
                      <AvatarFallback className="bg-primary/10 text-primary font-bold text-lg">
                        {founder.first_name ? founder.first_name[0].toUpperCase() : <Shield className="h-5 w-5" />}
                      </AvatarFallback>
                    </Avatar>

                    <p className="font-semibold text-foreground text-sm truncate">
                      {founder.first_name || 'Founding Member'}
                    </p>

                    <p className="text-[10px] text-muted-foreground mt-1">
                      {t.joinedLabel}{' '}
                      {new Date(founder.qualified_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : language, {
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Gratitude footer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
              className="text-center mt-16 py-8 border-t border-border/30"
            >
              <Heart className="h-5 w-5 text-primary mx-auto mb-3" fill="currentColor" />
              <p className="text-sm text-muted-foreground italic">{t.gratitude}</p>
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
}
